import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { KeyRound, Loader2, ArrowLeft } from "lucide-react";
import { pinSetup } from "../lib/api_extra";
import { useAuth } from "../contexts/AuthContext";

const SUGGESTED_QUESTIONS = [
  "Nome del primo cane",
  "Nome scuola elementare",
  "Città di nascita",
  "Nome del miglior amico d'infanzia",
];

export default function SetupPin() {
  const { user, refreshMe, logout } = useAuth();
  const nav = useNavigate();
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [domanda, setDomanda] = useState(SUGGESTED_QUESTIONS[0]);
  const [domandaCustom, setDomandaCustom] = useState("");
  const [risposta, setRisposta] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && !user.loginConPin) {
      // L'admin non ha abilitato PIN per questo utente → torna a casa
      toast.error("Login con PIN non abilitato dall'amministratore");
      nav("/giro", { replace: true });
    }
    if (user && user.loginConPin && user.pinSetUp) {
      // PIN già configurato → niente da fare
      nav("/giro", { replace: true });
    }
  }, [user, nav]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!/^[1-9]{4}$/.test(pin)) {
      toast.error("Il PIN deve essere 4 cifre da 1 a 9 (no zero)");
      return;
    }
    if (pin !== confirm) { toast.error("I due PIN non coincidono"); return; }
    if (!risposta.trim()) { toast.error("Risposta di recupero obbligatoria"); return; }
    setLoading(true);
    try {
      const finalQuestion = domandaCustom.trim() || domanda;
      await pinSetup(pin, finalQuestion, risposta.trim());
      toast.success("PIN configurato. Da ora puoi accedere col PIN.");
      await refreshMe();
      nav("/giro", { replace: true });
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Errore configurazione PIN");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#1B5E20]/5 via-white to-[#F57C00]/5 p-4" data-testid="setup-pin-page">
      <Card className="w-full max-w-md p-6 sm:p-8 shadow-xl">
        <div className="flex flex-col items-center mb-4">
          <div className="h-14 w-14 rounded-2xl bg-[#F57C00] flex items-center justify-center text-white mb-3">
            <KeyRound size={26} />
          </div>
          <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
            Configura il tuo PIN
          </h1>
          <p className="text-sm text-slate-500 mt-1 text-center">
            L'amministratore ha abilitato il login con PIN. Imposta un PIN di 4 cifre da 1 a 9.
          </p>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider">Nuovo PIN (4 cifre 1-9)</Label>
            <Input
              type="text" inputMode="numeric" maxLength={4} pattern="[1-9]{4}"
              value={pin} onChange={(e) => setPin(e.target.value.replace(/[^1-9]/g, ""))}
              required autoFocus
              className="h-14 text-center text-2xl tracking-[0.6em] font-bold"
              data-testid="setup-pin-input"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Conferma PIN</Label>
            <Input
              type="text" inputMode="numeric" maxLength={4} pattern="[1-9]{4}"
              value={confirm} onChange={(e) => setConfirm(e.target.value.replace(/[^1-9]/g, ""))}
              required
              className="h-14 text-center text-2xl tracking-[0.6em] font-bold"
              data-testid="setup-pin-confirm"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Domanda di recupero</Label>
            <select
              value={domanda} onChange={(e) => setDomanda(e.target.value)}
              className="h-12 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
              data-testid="setup-pin-question"
            >
              {SUGGESTED_QUESTIONS.map((q) => <option key={q} value={q}>{q}</option>)}
              <option value="__custom">Personalizzata...</option>
            </select>
            {domanda === "__custom" && (
              <Input
                value={domandaCustom} onChange={(e) => setDomandaCustom(e.target.value)}
                placeholder="Scrivi la tua domanda" className="h-12 mt-2"
                data-testid="setup-pin-question-custom"
              />
            )}
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Risposta</Label>
            <Input
              value={risposta} onChange={(e) => setRisposta(e.target.value)}
              required className="h-12" data-testid="setup-pin-answer"
            />
            <p className="text-[10px] text-slate-400 mt-1">Conserva la risposta in un posto sicuro. Servirà se dimentichi il PIN.</p>
          </div>
          <Button type="submit" disabled={loading}
            className="w-full h-12 bg-[#1B5E20] hover:bg-[#144818] text-white font-semibold min-h-[44px]"
            data-testid="setup-pin-submit"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : "Conferma PIN"}
          </Button>
          <button type="button" onClick={() => logout().then(() => nav("/login"))}
            className="w-full text-xs text-slate-500 hover:text-slate-800 pt-2"
          >
            <ArrowLeft size={12} className="inline mr-1" /> Esci
          </button>
        </form>
      </Card>
    </div>
  );
}
